import { Direction, getHighlightingDetails } from "@certa/studio-core";
import type { CallbackPayload } from "../engines/interactionEngine/interactionTypes";
import type { FSC } from "../engines/renderingEngine/fsc.types";
import {
  BoxNode,
  RhombusNode,
  TextNode
} from "../engines/renderingEngine/primitiveNodes";
import { SwimlaneNode } from "./SwimlaneNode";
import { THEME } from "./colorPalette.constants";
import { styles, processFlowNodeConstants } from "./scomponents.styles";
import { DragType } from "../engines/interactionEngine/interactionObserverConstants";
import { ComponentType } from "../..";
import { getHighlightingStyle } from "./scomponents.utils";
import {
  getFlowStepColumnKey,
  getFlowStepKey,
  getFlowSwimlaneColumnKey,
  getFlowSwimlaneDownwardsRuleIndicatorKey,
  getFlowSwimlaneKey,
  getFlowSwimlaneRowKey,
  getFlowSwimlaneUpwardsRuleIndicatorKey,
  getProcessNodeFlowKey,
  getSwimlaneIdFromFlowSwimlaneDownwardsRuleIndicatorKey,
  getSwimlaneIdFromFlowSwimlaneUpwardsRuleIndicatorKey
} from "../utils/general.utils";
import { NoChildren } from "./NoChildren";
import { EmptyProcess } from "./EmptyProcess";
import { Align, FlexDirection } from "yoga-layout";
import type { ProcessNodeProps } from "./ProcessNode";
import { StepNode } from "./StepNode";
import type {
  FlowLine,
  WaterfallLayout
} from "@certa/studio/src/hooks/processLookup/flowLayoutCalculator";
import type { DeepReadonly } from "@certa/types";
import { appState } from "../utils/store";

export type FlowNodeProps = DeepReadonly<{
  layoutData?: {
    waterfallView: WaterfallLayout;
  };
  flowLines?: FlowLine[];
}>;

type ProcessFlowNodeProps = ProcessNodeProps & FlowNodeProps;

const {
  STEP_WIDTH,
  STEP_ROW_GAP,
  STEP_HEIGHT,
  STEP_HEIGHT_READONLY,
  SWIMLANE_TITLE_AND_PADDING,
  SWIMLANE_TITLE_AND_PADDING_READONLY,
  SWIMLANE_ROW_GAP,
  SWIMLANE_RULE_INDICATOR_GAP,
  FLOW_SWIMLANE_RULE_MIN_WIDTH,
  MIN_SWIMLANE_WIDTH
} = processFlowNodeConstants;

export const ProcessFlowNode: FSC<ProcessFlowNodeProps> = ({
  process,
  collapsed,
  onCollapseToggle,
  selectedKey,
  onSelect,
  onDeselect,
  onPointerOver,
  onPointerOut,
  theme = THEME.LIGHT,
  currentDroppingId,
  highlightedKey,
  readonly,
  openRuleDrawer,
  getRuleIndicatorCount,
  onComponentDrag,
  componentsToHighlight,
  onTemplateDrop,
  layoutMode,
  layoutData,
  flowLines
}) => {
  if (!process || !process.process.swimlaneSequence) {
    return BoxNode({
      key: getProcessNodeFlowKey("Empty"),
      style: {},
      children: []
    });
  }
  const hoveredComponentKey = componentsToHighlight.hoveredComponent;
  const stepHeight = readonly ? STEP_HEIGHT_READONLY : STEP_HEIGHT;
  const swimlaneTitleAndPadding = readonly
    ? SWIMLANE_TITLE_AND_PADDING_READONLY
    : SWIMLANE_TITLE_AND_PADDING;
  const handleCollapseToggle = (key: string, event: CallbackPayload): void => {
    onCollapseToggle?.({
      nodeId: key,
      collapse: !collapsed.includes(key)
    });
  };

  // return no swimlanes
  if (!layoutData?.waterfallView?.swimlanes?.length) {
    return BoxNode({
      key: getProcessNodeFlowKey(`${process.process.id}`),
      style: {
        ...styles[theme].process.container,
        flexDirection: FlexDirection.Column
      },
      eventHandlers: {
        onClick: () => {
          onDeselect?.();
        }
      },
      dropTypes: [
        DragType.TEMPLATE_SWIMLANE,
        DragType.TEMPLATE_MODULE,
        DragType.TEMPLATE_PROCESS
      ],
      children: [EmptyProcess()]
    });
  }

  const style = styles[theme];

  const shouldShowStepFlowLines: boolean =
    appState.shouldShowStepFlowLines ?? false;

  const flowStepNodeMap = new Map(
    (layoutData?.waterfallView.steps ?? []).map(node => [node.id, node])
  );
  const flowSwimlaneNodeMap = new Map(
    (layoutData?.waterfallView.swimlanes ?? []).map(node => [node.id, node])
  );

  const swimlaneRowIndexWithStepsMaxRowIndex = new Map<number, number>();
  const swimlaneIdWithStepsByColumn = new Map<
    string,
    Record<number, (typeof process.steps)[string][]>
  >();

  // Group swimlanes by column
  const swimlanesByColumn = process.process.swimlaneSequence.reduce<
    Record<number, (typeof process.swimlanes)[string][]>
  >((acc, swimlaneId) => {
    const swimlane = process.swimlanes[swimlaneId];
    if (!swimlane) {
      return acc;
    }

    const swimlaneLayoutData = flowSwimlaneNodeMap.get(swimlaneId);
    const column = swimlaneLayoutData ? swimlaneLayoutData.column : 0;
    const swimlaneRowId = swimlaneLayoutData ? swimlaneLayoutData.row : 0;

    // Group steps by column
    const stepsByColumn = swimlane.steps.reduce(
      (acc, stepId) => {
        const step = process.steps[stepId];
        if (!step) {
          return acc;
        }

        const stepLayoutData = flowStepNodeMap.get(step.id);
        const column = stepLayoutData ? stepLayoutData.column : 0;

        if (!acc[column]) {
          acc[column] = [];
        }
        acc[column].push(step);
        return acc;
      },
      {} as Record<number, (typeof process.steps)[string][]>
    );
    let maxNoOfStepsInColumn = 0;
    Object.values(stepsByColumn).forEach(steps => {
      if (steps.length > maxNoOfStepsInColumn) {
        maxNoOfStepsInColumn = steps.length;
      }
    });
    swimlaneIdWithStepsByColumn.set(swimlaneId, stepsByColumn);
    swimlaneRowIndexWithStepsMaxRowIndex.set(
      swimlaneRowId,
      shouldShowStepFlowLines ? swimlane.steps.length : maxNoOfStepsInColumn
    );

    if (!acc[column]) {
      acc[column] = [];
    }
    acc[column].push(swimlane);
    return acc;
  }, {});

  const swimlanesColumnBoxes = Object.entries(swimlanesByColumn).map(
    ([column, columnSwimlanes]) => {
      let swimlaneLastRowIndex: number | undefined = undefined;
      const swimlanes = columnSwimlanes.map((swimlane, swimlaneIndex) => {
        if (!swimlane) {
          return BoxNode({
            key: getFlowSwimlaneKey(`Empty-${swimlaneIndex}`),
            style: style.zeroHeight,
            children: []
          });
        }
        const swimlaneKey = getFlowSwimlaneKey(swimlane.id);
        const swimlaneSequenceIndex = process.process.swimlaneSequence.indexOf(
          swimlane.id
        );
        const rowIndex = flowSwimlaneNodeMap.get(swimlane.id)?.row ?? 0;
        const marginTop = getMarginTopForSwimlane({
          currentRowIndex: rowIndex,
          lastRowIndex: swimlaneLastRowIndex,
          swimlaneRowIndexWithStepsMaxRowIndex,
          stepHeight,
          swimlaneTitleAndPadding
        });
        swimlaneLastRowIndex = rowIndex;

        const {
          upStreamRulesCount: swimlaneUpStreamCount,
          downStreamRulesCount: swimlaneDownStreamCount
        } = getRuleIndicatorCount(swimlane.id);

        const swimlaneComponentAction = getHighlightingDetails(
          swimlane.tag,
          componentsToHighlight
        );
        const swimlaneStyleOverride = getHighlightingStyle(
          swimlaneComponentAction?.changeType,
          theme
        );

        // Group steps by column
        const stepsByColumn =
          swimlaneIdWithStepsByColumn.get(swimlane.id) ?? {};

        const columnBoxes = Object.entries(stepsByColumn).map(
          ([column, columnSteps]) => {
            let lastRowIndex: number | undefined = undefined;
            const steps = columnSteps.map(step => {
              const stepKey = getFlowStepKey(step.id);
              const stepLayoutData = flowStepNodeMap.get(step.id);
              const stepSequenceIndex = swimlane.steps.indexOf(step.id);
              const rowId = stepLayoutData?.row ?? 0;
              const noOfRowsBetween = getNoOfRowsBetween(rowId, lastRowIndex);
              lastRowIndex = rowId;
              const {
                upStreamRulesCount: stepUpStreamCount,
                downStreamRulesCount: stepDownStreamCount
              } = getRuleIndicatorCount(step.id);

              const stepComponentAction = getHighlightingDetails(
                step.tag,
                componentsToHighlight
              );
              const stepStyleOverride = getHighlightingStyle(
                stepComponentAction?.changeType,
                theme
              );
              const titlePrefix = shouldShowStepFlowLines
                ? `${swimlaneSequenceIndex + 1}.${stepSequenceIndex + 1} `
                : "";

              // Note: This is temporary fix for the performance issue that we are facing with the border width.
              // We are using the same border color for the flow container and the step node container.
              // This is not a good practice and we need to find a better solution in the future.
              return BoxNode({
                key: stepKey + "-container",
                style: {
                  flexDirection: FlexDirection.Column,
                  backgroundColor: style.step?.flowContainer?.borderColor,
                  borderRadius: style.step?.flowContainer?.borderRadius,
                  flexShrink: 1,
                  flexGrow: 1,
                  paddingLeft: 1,
                  paddingRight: 1,
                  paddingTop: 1,
                  paddingBottom: 1,
                  minWidth: STEP_WIDTH + 2,
                  maxWidth: STEP_WIDTH + 2,
                  marginTop: shouldShowStepFlowLines
                    ? noOfRowsBetween * stepHeight
                    : 0 // this needs to be changed
                },
                children: [
                  StepNode({
                    dropIndicatorIndex: -1,
                    isDroppingAbove: false,
                    key: stepKey,
                    theme,
                    swimlaneId: swimlaneKey,
                    title: titlePrefix + step.name,
                    expanded: !collapsed.includes(stepKey),
                    onCollapseToggle: handleCollapseToggle.bind(null, stepKey),
                    upStreamRulesCount: stepUpStreamCount,
                    downStreamRulesCount: stepDownStreamCount,
                    onUpStreamRuleIndicatorClick: () => {
                      onSelect?.(stepKey);
                      openRuleDrawer(
                        ComponentType.STEP,
                        step.id,
                        Direction.INCOMING
                      );
                    },
                    onDownStreamRuleIndicatorClick: () => {
                      onSelect?.(stepKey);
                      openRuleDrawer(
                        ComponentType.STEP,
                        step.id,
                        Direction.OUTGOING
                      );
                    },
                    styleOverride: {
                      ...stepStyleOverride,
                      minWidth: STEP_WIDTH,
                      maxWidth: STEP_WIDTH,
                      alignSelf: Align.FlexStart
                    },
                    children: [],
                    readonly,
                    disableExpandAndCollapse: true,
                    selected:
                      currentDroppingId === stepKey ||
                      selectedKey === stepKey ||
                      highlightedKey === stepKey,
                    onClick: event => {
                      onSelect?.(stepKey);
                    },
                    onDropped: event => {
                      // Handle the drop event
                    },
                    onDragStart: event => {
                      // Handle the drag start event
                    },
                    viewType: shouldShowStepFlowLines
                      ? "swimlaneFlow"
                      : "workflowFlow"
                  })
                ]
              });
            });

            return BoxNode({
              key: getFlowStepColumnKey(swimlane.id, column),
              style: {
                flexDirection: FlexDirection.Column,
                flexShrink: 1,
                flexGrow: 1,
                alignSelf: Align.FlexStart,
                alignItems: Align.FlexStart,
                gapRow: STEP_ROW_GAP
              },
              children: steps.length ? steps : []
            });
          }
        );
        const titlePrefix = `${swimlaneSequenceIndex + 1}.`;
        const { isSelected: isSelectedByRuleIndicator, swimlaneIsSelectedBy } =
          getIsSelectedByRuleIndicator({
            selectedKey,
            hoveredComponentKey,
            swimlaneId: swimlane.id,
            upStreamFlowLinesKey:
              flowSwimlaneNodeMap.get(swimlane.id)?.upStreamData
                ?.flowLinesKeys ?? [],
            downStreamFlowLines:
              flowSwimlaneNodeMap.get(swimlane.id)?.downStreamData
                ?.flowLinesKeys ?? []
          });
        const swimlaneNode = SwimlaneNode({
          layoutMode,
          dropIndicatorIndex: -1,
          key: swimlaneKey,
          isDroppingBefore: false,
          theme,
          readonly,
          disableExpandAndCollapse: true,
          selected:
            currentDroppingId === swimlaneKey ||
            selectedKey === swimlaneKey ||
            highlightedKey === swimlaneKey ||
            isSelectedByRuleIndicator,
          expanded: !collapsed.includes(swimlaneKey),
          upStreamRulesCount: swimlaneUpStreamCount,
          downStreamRulesCount: swimlaneDownStreamCount,
          onUpStreamRuleIndicatorClick: () => {
            onSelect?.(swimlaneKey);
            openRuleDrawer(
              ComponentType.SWIMLANE,
              swimlane.id,
              Direction.INCOMING
            );
          },
          styleOverride: {
            ...swimlaneStyleOverride,
            flexShrink: 1,
            width: "auto",
            minWidth: MIN_SWIMLANE_WIDTH
          },
          onDownStreamRuleIndicatorClick: () => {
            onSelect?.(swimlaneKey);
            openRuleDrawer(
              ComponentType.SWIMLANE,
              swimlane.id,
              Direction.OUTGOING
            );
          },
          children:
            columnBoxes.length > 0
              ? columnBoxes
              : [
                  readonly
                    ? NoChildren({
                        key: getFlowSwimlaneKey(`Empty-${swimlane.id}`),
                        title: "Empty swimlane"
                      })
                    : BoxNode({
                        key: getFlowSwimlaneKey(`Empty-${swimlane.id}`),
                        style: style.zeroHeight,
                        children: []
                      })
                ],
          title: titlePrefix + " " + swimlane.name,
          onDragStart: event => {},
          onDropped: event => {},
          onClick: event => {
            onSelect?.(swimlaneKey);
          },
          onCollapseToggle: handleCollapseToggle.bind(null, swimlaneKey)
        });

        const rowChildren = [];

        const {
          dependentStepIds: upStreamFlowLinesDependentStepIds = [],
          ruleDetails: upStreamFlowLinesRuleDetails = [],
          totalRules: totalUpStreamRules = 0,
          hasRules: hasUpStreamRules = false
        } = flowSwimlaneNodeMap.get(swimlane.id)?.upStreamData ?? {};

        const {
          dependentStepIds: downStreamFlowLinesDependentStepIds = [],
          ruleDetails: downStreamFlowLinesRuleDetails = [],
          totalRules: totalDownStreamRules = 0,
          hasRules: hasDownStreamRules = false
        } = flowSwimlaneNodeMap.get(swimlane.id)?.downStreamData ?? {};

        if (hasUpStreamRules) {
          const key = getFlowSwimlaneUpwardsRuleIndicatorKey(swimlane.id);
          const isSelected = getIsRuleIndicatorSelected({
            selectedKey,
            hoveredComponentKey,
            swimlaneIsSelectedBy,
            ruleIndicatorKey: key,
            ruleIndicatorDirection: "upStreamRuleIndicator"
          });
          rowChildren.push(
            RhombusNode({
              key,
              style: isSelected
                ? style.flowSwimlaneSelectedRuleIndicator
                : style.flowSwimlaneRuleIndicator,
              eventHandlers: {
                onClick: () => {
                  onSelect?.(key);
                  openRuleDrawer(
                    ComponentType.SWIMLANE,
                    swimlane.id,
                    Direction.INCOMING,
                    upStreamFlowLinesRuleDetails,
                    upStreamFlowLinesDependentStepIds
                  );
                },
                onPointerOver: () => {
                  onPointerOver(key);
                },
                onPointerOut
              },
              children: [
                TextNode({
                  key: `${getFlowSwimlaneUpwardsRuleIndicatorKey(swimlane.id)}-text`,
                  text: totalUpStreamRules.toString(),
                  textStyle: style.flowSwimlaneRuleIndicatorText,
                  children: []
                })
              ]
            })
          );
        }
        rowChildren.push(swimlaneNode);
        if (hasDownStreamRules) {
          const key = getFlowSwimlaneDownwardsRuleIndicatorKey(swimlane.id);
          const isSelected = getIsRuleIndicatorSelected({
            selectedKey,
            hoveredComponentKey,
            swimlaneIsSelectedBy,
            ruleIndicatorKey: key,
            ruleIndicatorDirection: "downStreamRuleIndicator"
          });
          rowChildren.push(
            RhombusNode({
              key,
              style: isSelected
                ? style.flowSwimlaneSelectedRuleIndicator
                : style.flowSwimlaneRuleIndicator,
              eventHandlers: {
                onClick: () => {
                  onSelect?.(key);
                  openRuleDrawer(
                    ComponentType.SWIMLANE,
                    swimlane.id,
                    Direction.OUTGOING,
                    downStreamFlowLinesRuleDetails,
                    downStreamFlowLinesDependentStepIds
                  );
                },
                onPointerOver: () => {
                  onPointerOver(key);
                },
                onPointerOut
              },
              children: [
                TextNode({
                  key: `${getFlowSwimlaneDownwardsRuleIndicatorKey(swimlane.id)}-text`,
                  text: totalDownStreamRules.toString(),
                  textStyle: style.flowSwimlaneRuleIndicatorText,
                  children: []
                })
              ]
            })
          );
        }

        return BoxNode({
          key: getFlowSwimlaneRowKey(`${rowIndex}`),
          style: {
            marginTop,
            flexDirection: FlexDirection.Row,
            alignItems: Align.Center,
            gapColumn: SWIMLANE_RULE_INDICATOR_GAP,
            paddingLeft: getSwimlaneLeftRightPadding(hasUpStreamRules),
            paddingRight: getSwimlaneLeftRightPadding(hasDownStreamRules)
          },
          children: rowChildren
        });
      });
      return BoxNode({
        key: getFlowSwimlaneColumnKey(column),
        style: {
          flexDirection: FlexDirection.Column,
          flexShrink: 1,
          flexGrow: 1,
          alignSelf: Align.FlexStart,
          alignItems: Align.FlexStart,
          gapRow: SWIMLANE_ROW_GAP
        },
        eventHandlers: {
          onClick: () => {
            // Deselect in the empty area of the imaginary column layout.
            onDeselect?.();
          }
        },
        children: swimlanes.length ? swimlanes : []
      });
    }
  );

  if (process.process.swimlaneSequence.length === 0) {
    // children.push(EmptyProcess());
  }

  return BoxNode({
    key: getProcessNodeFlowKey(`${process.process.id}`),
    style: {
      ...style.process.container,
      flexDirection: FlexDirection.Row,
      alignItems: Align.FlexStart
    },
    eventHandlers: {
      onClick: () => {
        onDeselect?.();
      }
    },
    dropTypes: [
      DragType.TEMPLATE_SWIMLANE,
      DragType.TEMPLATE_MODULE,
      DragType.TEMPLATE_PROCESS
    ],
    children: swimlanesColumnBoxes
  });
};

const calculateSwimlaneRowTop = (
  rowIndex: number,
  swimlaneRowIndexWithStepsMaxRowIndex: Map<number, number>,
  stepHeight: number,
  swimlaneTitleAndPadding: number
) => {
  let totalSteps = 0;
  for (let i = 0; i < rowIndex; i++) {
    totalSteps += swimlaneRowIndexWithStepsMaxRowIndex.get(i) ?? 0;
  }

  return (
    totalSteps * stepHeight +
    rowIndex * swimlaneTitleAndPadding +
    rowIndex * SWIMLANE_ROW_GAP
  );
};

type GetMarginTopForSwimlaneProps = {
  currentRowIndex: number;
  lastRowIndex: number | undefined;
  swimlaneRowIndexWithStepsMaxRowIndex: Map<number, number>;
  stepHeight: number;
  swimlaneTitleAndPadding: number;
};

const getMarginTopForSwimlane = ({
  currentRowIndex,
  lastRowIndex,
  swimlaneRowIndexWithStepsMaxRowIndex,
  stepHeight,
  swimlaneTitleAndPadding
}: GetMarginTopForSwimlaneProps) => {
  if (currentRowIndex === 0) {
    return 0;
    // this is true for swimlane which is first in it's column but column is not 0
  } else if (lastRowIndex === undefined) {
    const currentRowTop = calculateSwimlaneRowTop(
      currentRowIndex,
      swimlaneRowIndexWithStepsMaxRowIndex,
      stepHeight,
      swimlaneTitleAndPadding
    );

    return currentRowTop;
  }
  const lastRowTop = calculateSwimlaneRowTop(
    lastRowIndex,
    swimlaneRowIndexWithStepsMaxRowIndex,
    stepHeight,
    swimlaneTitleAndPadding
  );

  const noOfStepsInLastRow =
    swimlaneRowIndexWithStepsMaxRowIndex.get(lastRowIndex) ?? 0;

  const lastRowHeight =
    noOfStepsInLastRow * stepHeight +
    swimlaneTitleAndPadding +
    SWIMLANE_ROW_GAP;

  const currentRowTop = calculateSwimlaneRowTop(
    currentRowIndex,
    swimlaneRowIndexWithStepsMaxRowIndex,
    stepHeight,
    swimlaneTitleAndPadding
  );

  return currentRowTop - (lastRowTop + lastRowHeight);
};

const getNoOfRowsBetween = (
  currentRowIndex: number,
  lastRowIndex: number | undefined
) => {
  if (currentRowIndex === 0) {
    return 0;
  }
  return lastRowIndex === undefined
    ? currentRowIndex
    : currentRowIndex - lastRowIndex - 1;
};

const getSwimlaneLeftRightPadding = (hasRules: boolean) => {
  if (hasRules) {
    return SWIMLANE_RULE_INDICATOR_GAP;
  }
  return 2 * SWIMLANE_RULE_INDICATOR_GAP + FLOW_SWIMLANE_RULE_MIN_WIDTH;
};

type GetIsSelectedByRuleIndicatorProps = {
  selectedKey: string;
  hoveredComponentKey: string | null;
  swimlaneId: string;
  upStreamFlowLinesKey: readonly string[];
  downStreamFlowLines: readonly string[];
};

type GetIsSelectedByRuleIndicator = {
  isSelected: boolean;
  swimlaneIsSelectedBy:
    | "upStreamRuleIndicator"
    | "downStreamRuleIndicator"
    | undefined;
};

const getIsSelectedByRuleIndicator = ({
  selectedKey,
  hoveredComponentKey,
  swimlaneId,
  upStreamFlowLinesKey,
  downStreamFlowLines
}: GetIsSelectedByRuleIndicatorProps): GetIsSelectedByRuleIndicator => {
  let isSelected = false;
  let swimlaneIsSelectedBy: GetIsSelectedByRuleIndicator["swimlaneIsSelectedBy"];
  const key = hoveredComponentKey ?? selectedKey;

  const selectedSourceSwimlaneId =
    getSwimlaneIdFromFlowSwimlaneDownwardsRuleIndicatorKey(key);

  if (selectedSourceSwimlaneId) {
    if (selectedSourceSwimlaneId === swimlaneId) {
      isSelected = true;
      swimlaneIsSelectedBy = "downStreamRuleIndicator";
    } else {
      isSelected = upStreamFlowLinesKey.some(key =>
        key.includes(selectedSourceSwimlaneId)
      );
      swimlaneIsSelectedBy = isSelected ? "upStreamRuleIndicator" : undefined;
    }
  } else {
    const selectedTargetSwimlaneId =
      getSwimlaneIdFromFlowSwimlaneUpwardsRuleIndicatorKey(key);
    if (selectedTargetSwimlaneId) {
      if (selectedTargetSwimlaneId === swimlaneId) {
        isSelected = true;
        swimlaneIsSelectedBy = "upStreamRuleIndicator";
      } else {
        isSelected = downStreamFlowLines.some(key =>
          key.includes(selectedTargetSwimlaneId)
        );
        swimlaneIsSelectedBy = isSelected
          ? "downStreamRuleIndicator"
          : undefined;
      }
    }
  }
  return { isSelected, swimlaneIsSelectedBy };
};

type GetIsRuleIndicatorSelectedProps = {
  selectedKey: string;
  hoveredComponentKey: string | null;
  swimlaneIsSelectedBy: GetIsSelectedByRuleIndicator["swimlaneIsSelectedBy"];
  ruleIndicatorDirection: GetIsSelectedByRuleIndicator["swimlaneIsSelectedBy"];
  ruleIndicatorKey: string;
};

const getIsRuleIndicatorSelected = ({
  selectedKey,
  hoveredComponentKey,
  swimlaneIsSelectedBy,
  ruleIndicatorKey,
  ruleIndicatorDirection
}: GetIsRuleIndicatorSelectedProps): boolean => {
  const key = hoveredComponentKey ?? selectedKey;

  if (key === ruleIndicatorKey) {
    return true;
  }
  return swimlaneIsSelectedBy === ruleIndicatorDirection;
};
